import React, { useState, useEffect } from 'react';
import { FaChartBar, FaSpinner } from 'react-icons/fa';
import API_URL from '../config';

const CATEGORIES = [
  { key: 'population', label: 'Population', color: '#4F46E5' },
  { key: 'economy', label: 'Economy', color: '#10B981' },
  { key: 'health', label: 'Health', color: '#EF4444' },
  { key: 'education', label: 'Education', color: '#8B5CF6' },
];

const METRICS = {
  population: [
    { key: 'total_population', label: 'Total Population (M)' },
    { key: 'growth_rate', label: 'Growth Rate (%)' },
    { key: 'urban_population', label: 'Urban Population (M)' },
    { key: 'median_age', label: 'Median Age' },
  ],
  economy: [
    { key: 'gdp_growth', label: 'GDP Growth (%)' },
    { key: 'inflation_rate', label: 'Inflation Rate (%)' },
    { key: 'unemployment_rate', label: 'Unemployment Rate (%)' },
    { key: 'gdp_nominal', label: 'GDP Nominal (B)' },
    { key: 'exchange_rate_usd', label: 'Exchange Rate (KES/USD)' },
    { key: 'interest_rate', label: 'Interest Rate (%)' },
  ],
  health: [
    { key: 'life_expectancy', label: 'Life Expectancy (yrs)' },
    { key: 'infant_mortality_rate', label: 'Infant Mortality Rate' },
    { key: 'maternal_mortality_rate', label: 'Maternal Mortality Rate' },
    { key: 'hiv_prevalence', label: 'HIV Prevalence (%)' },
    { key: 'immunization_coverage', label: 'Immunization Coverage (%)' },
    { key: 'healthcare_access', label: 'Healthcare Access (%)' },
  ],
  education: [
    { key: 'literacy_rate', label: 'Literacy Rate (%)' },
    { key: 'primary_enrollment', label: 'Primary Enrollment (%)' },
    { key: 'secondary_enrollment', label: 'Secondary Enrollment (%)' },
    { key: 'tertiary_enrollment', label: 'Tertiary Enrollment (%)' },
  ],
};

function Compare() {
  const [category, setCategory] = useState('economy');
  const [metric, setMetric] = useState('gdp_growth');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedYears, setSelectedYears] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  // Fetch data when category changes
  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/${category}`)
      .then(res => res.json())
      .then(result => {
        if (result.status === 'success') {
          setData(result.data);
          const years = [...new Set(result.data.map(d => d.year))].sort((a, b) => b - a);
          setAvailableYears(years);
          setSelectedYears(years.slice(0, 3));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [category]);

  // Update metric when category changes
  useEffect(() => {
    setMetric(METRICS[category][0].key);
  }, [category]);

  const toggleYear = (year) => {
    if (selectedYears.includes(year)) {
      if (selectedYears.length > 1) {
        setSelectedYears(selectedYears.filter(y => y !== year));
      }
    } else {
      setSelectedYears([...selectedYears, year].sort((a, b) => b - a));
    }
  };

  // Get comparison data
  const comparisonData = data.filter(d => selectedYears.includes(d.year));

  // Get max value for bar chart scaling
  const maxValue = Math.max(...comparisonData.map(d => d[metric] || 0));

  const categoryColor = CATEGORIES.find(c => c.key === category)?.color || '#4F46E5';
  const metricLabel = METRICS[category].find(m => m.key === metric)?.label || metric;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <FaChartBar className="text-4xl text-indigo-600 mr-4" />
            <h1 className="text-4xl font-bold text-gray-900">Compare Data</h1>
          </div>
          <p className="text-gray-600 text-lg">
            Compare Kenya statistics across different years and categories
          </p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Comparison Parameters</h3>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Category Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setCategory(cat.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      category === cat.key
                        ? 'text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={category === cat.key ? { backgroundColor: cat.color } : {}}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Metric Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Metric
              </label>
              <select
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400 text-sm"
              >
                {METRICS[category].map(m => (
                  <option key={m.key} value={m.key}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Year Selector */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Years to Compare
            </label>
            <div className="flex flex-wrap gap-2">
              {availableYears.map(year => (
                <button
                  key={year}
                  onClick={() => toggleYear(year)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    selectedYears.includes(year)
                      ? 'text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={selectedYears.includes(year) ? { backgroundColor: categoryColor } : {}}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <FaSpinner className="text-5xl text-indigo-600 animate-spin" />
          </div>
        ) : (
          <>
            {/* Bar Chart */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {metricLabel} - Year on Year Comparison
              </h3>
              <div className="space-y-4">
                {comparisonData
                  .sort((a, b) => a.year - b.year)
                  .map((row, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <span className="text-sm font-medium text-gray-700 w-16 text-right">
                        {row.year}
                        {row.quarter ? ` ${row.quarter}` : ''}
                      </span>
                      <div className="flex-1 bg-gray-100 rounded-full h-8 relative">
                        <div
                          className="h-8 rounded-full flex items-center justify-end pr-3 transition-all duration-500"
                          style={{
                            width: `${maxValue > 0 ? (row[metric] / maxValue) * 100 : 0}%`,
                            backgroundColor: categoryColor,
                            minWidth: '60px'
                          }}
                        >
                          <span className="text-white text-sm font-semibold">
                            {row[metric]}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Comparison Table */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Detailed Comparison Table
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-gray-600">Metric</th>
                      {comparisonData
                        .sort((a, b) => a.year - b.year)
                        .map((row, index) => (
                          <th key={index} className="px-4 py-3 text-left text-gray-600">
                            {row.year}{row.quarter ? ` ${row.quarter}` : ''}
                          </th>
                        ))}
                      <th className="px-4 py-3 text-left text-gray-600">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {METRICS[category].map((m, index) => {
                      const values = comparisonData
                        .sort((a, b) => a.year - b.year)
                        .map(row => row[m.key]);
                      const first = values[0];
                      const last = values[values.length - 1];
                      const change = first && last ? ((last - first) / first * 100).toFixed(1) : null;

                      return (
                        <tr key={index} className={`border-t hover:bg-gray-50 ${m.key === metric ? 'bg-indigo-50' : ''}`}>
                          <td className="px-4 py-3 font-medium text-gray-700">{m.label}</td>
                          {values.map((val, i) => (
                            <td key={i} className="px-4 py-3 font-semibold" style={{ color: categoryColor }}>
                              {val ?? 'N/A'}
                            </td>
                          ))}
                          <td className="px-4 py-3">
                            {change !== null && (
                              <span className={`text-sm font-medium ${parseFloat(change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {parseFloat(change) >= 0 ? '+' : ''}{change}%
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid md:grid-cols-3 gap-6">
              {comparisonData
                .sort((a, b) => a.year - b.year)
                .map((row, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-md p-6">
                    <div
                      className="text-white text-center py-2 rounded-lg mb-4 font-semibold"
                      style={{ backgroundColor: categoryColor }}
                    >
                      {row.year}{row.quarter ? ` ${row.quarter}` : ''}
                    </div>
                    <div className="space-y-2">
                      {METRICS[category].slice(0, 4).map((m, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-600">{m.label}</span>
                          <span className="font-semibold text-gray-900">{row[m.key] ?? 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}

        {/* Data Source */}
        <div className="mt-8 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
          <p className="text-sm text-gray-700">
            <strong>Data Sources:</strong> Kenya National Bureau of Statistics (KNBS),
            Central Bank of Kenya (CBK), World Bank, WHO, UNESCO
          </p>
        </div>
      </div>
    </div>
  );
}

export default Compare;